import _ from 'lodash';
import { Inline } from 'components/base/Text';
import {
  getColor,
  getHighlightColors,
  hexToRGBA
} from 'common/constants/_colors';
import { Cell } from 'recharts';
import { METRICS, DAYS_HOURS_FORMAT } from 'common/constants/constants';
import {
  convertMinutesToTotalDisplay,
  getTableBasicConfig,
  formatBaseData,
  ISSUE_STATES,
  FILTERS
} from './common';
import moment from 'moment';

const CONSTANT_TABLE_FIELDS = {
  CUMULATIVE_TIME: 'Cumulative time'
};
const BAR_STATES_TO_IGNORE_IN_CHART = [CONSTANT_TABLE_FIELDS.CUMULATIVE_TIME];
const barSize = 30;

const CHART_CONFIG = {
  xAxis: { dataKey: 'name' },
  yAxis: {
    label: { value: 'Hours', angle: -90, position: 'insideLeft' }
  },
  gridConfig: { stroke: getColor('gray', 0.2) },
  bars: [],
  barFn: (data, { fill }) => {
    return data.map((entry, idx) => {
      const color =
        idx === data.length - 1 && entry.state === 'active'
          ? fill
          : hexToRGBA(fill, 0.7);
      return <Cell key={idx} fill={color} />;
    });
  },
  lines: []
};

const TimeDistributionService = () => {
  const formatData = rawData => {
    const formattedData = formatBaseData(rawData).map(iteration => {
      return {
        ...iteration,
        timeDistribution: iteration.stats.issue_state_stats.reduce(
          (acc, issue) => {
            acc[issue.issue_state] = _.round(
              issue.total_duration / 60 / issue.num_of_issues,
              1
            ); // Convert to hours
            return acc;
          },
          {}
        )
      };
    });

    return _(formattedData).sortBy('start', 'asc').value();
  };

  // Getters
  const getChartConfig = (slicedIterations, issues, chartBarsDef) => {
    return {
      ...CHART_CONFIG,
      xAxis: {
        dataKey: 'name',
        label: {
          value: 'Iterations',
          offset: -5,
          position: 'insideBottom'
        }
      },
      bars: chartBarsDef
        .filter(({ dataKey }) => issues === 'all' || dataKey === issues)
        .filter(({ name }) => !BAR_STATES_TO_IGNORE_IN_CHART.includes(name))
    };
  };

  const getTableData = iterations => {
    const uniqueFields = _.chain(iterations)
      .map(({ name, stats }) => {
        return [
          name,
          ...stats.issue_state_stats.map(({ issue_state }) => issue_state)
        ];
      })
      .flatten()
      .uniq()
      .reduce((acc, key) => {
        acc[key] = null;
        return acc;
      }, {})
      .value();

    const calculateColumnData = (acc, stats) => {
      const duration = moment.duration(
        parseFloat(stats.total_duration / stats.num_of_issues),
        'minutes'
      );
      acc[stats.issue_state] = duration.format(DAYS_HOURS_FORMAT);
      acc[CONSTANT_TABLE_FIELDS.CUMULATIVE_TIME] += duration ?? 0;
      return acc;
    };

    const resolveColumnDisplayValue = (acc, val, key) => {
      acc[key] = (
        <Inline size='medium'>
          {key === CONSTANT_TABLE_FIELDS.CUMULATIVE_TIME && val
            ? _.isNumber(val)
              ? moment.duration(val).format(DAYS_HOURS_FORMAT)
              : '-'
            : val || '-'}
        </Inline>
      );
      return acc;
    };

    const formatRow = ({ name, stats }) => {
      return _.reduce(
        {
          ...uniqueFields,
          name,
          ..._.reduce(stats.issue_state_stats, calculateColumnData, {
            [CONSTANT_TABLE_FIELDS.CUMULATIVE_TIME]: moment.duration(0)
          })
        },
        resolveColumnDisplayValue,
        {}
      );
    };

    return _.chain(iterations).orderBy('start', 'desc').map(formatRow).value();
  };

  const getDynamicStates = iterations => {
    const lastItem = 'Cumulative time';

    const uniqCols = _.chain(iterations)
      .map(({ stats }) => {
        return stats.issue_state_stats.map(({ issue_state }) => issue_state);
      })
      .flatten()
      .uniq()
      .reduce((acc, item) => {
        acc[item] = 0;
        return acc;
      }, {})
      .value();

    const relevantCols = _.chain(iterations)
      .reduce((acc, iteration) => {
        iteration.stats.issue_state_stats.forEach(issue => {
          acc[issue.issue_state] += parseInt(issue.total_duration);
        });
        return acc;
      }, uniqCols)
      .reduce((acc, val, key) => {
        if (val > 0) {
          acc.push(key);
        }
        return acc;
      }, [])
      .value();

    return [
      ..._.chain(iterations)
        .map(({ stats }) =>
          stats.issue_state_stats.map(({ issue_state }) => issue_state)
        )
        .flatten()
        .uniq()
        .filter(item => item !== lastItem && relevantCols.includes(item))
        .value(),
      lastItem
    ];
  };

  const getChartBarsDef = iterations => {
    const dynamicStates = getDynamicStates(iterations);

    return dynamicStates.length === 1
      ? []
      : dynamicStates.map((state, idx) => {
          const colorNames = getHighlightColors();

          const colorName =
            colorNames[idx] || colorNames[idx - colorNames.length];
          return {
            stackId: 'dynamicStates',
            dataKey: `timeDistribution['${state}']`,
            name: state,
            fill: getColor(colorName),
            color: colorName,
            barSize
          };
        });
  };

  const getFilterByIssuesParams = chartBarsDef => {
    const issueTypes = [
      { value: 'all', displayValue: 'All States' },
      ...chartBarsDef
        .filter(({ name }) => !BAR_STATES_TO_IGNORE_IN_CHART.includes(name))
        .map(({ name, dataKey }) => {
          return {
            value: dataKey,
            displayValue: name
          };
        })
    ];

    return {
      placeholder: 'All States',
      options: issueTypes
    };
  };

  const getFilterByIterationsParam = () => {
    const timeFrameOptions = METRICS.TIME_FRAME_FILTER_OPTIONS.map(
      timeFrame => ({
        value: timeFrame,
        displayValue: `Last ${timeFrame} iterations`
      })
    );

    return {
      placeholder: 'Last 5 iterations',
      options: timeFrameOptions
    };
  };

  const getApiAdditionalFilters = () => ({
    [FILTERS.ISSUE_STATE]: [ISSUE_STATES.IN_PROGRESS, ISSUE_STATES.CREATED]
  });
  return {
    formatData,
    convertMinutesToTotalDisplay,
    getChartConfig,
    getTableConfig: (totalDurationPerIssueType, chartBarsDef) =>
      getTableBasicConfig(totalDurationPerIssueType, chartBarsDef),
    getTableData,
    hidePie: true,
    graphTitle: '',
    getChartBarsDef,
    getDynamicStates,
    getFilterByIssuesParams,
    getFilterByIterationsParam,
    getApiAdditionalFilters
  };
};

export default TimeDistributionService;
